import React, { useState } from "react";
import { CreditCard, RefreshCw, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useMySubscription, useMyOrders, useBillingActions } from "@/hooks/useBilling";
import { SubStatusBadge } from "@/components/settings/SubStatusBadge";
import { CryptoCheckout } from "@/components/settings/CryptoCheckout";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-1.5 text-lg font-display font-semibold", accent)}>{value}</div>
    </div>
  );
}

export function BillingTenant({ compact }) {
  const { data, isLoading } = useMySubscription();
  const { data: ordersData } = useMyOrders();
  const { createOrder } = useBillingActions();
  const [selected, setSelected] = useState(null);
  const [checkout, setCheckout] = useState({ open: false, orderId: null });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading billing…</div>;
  const sub = data?.subscription;
  const plans = data?.plans || [];
  const orders = ordersData?.items || [];
  const openOrder = orders.find((o) => ["pending", "needs_tx_hash"].includes(o.status));

  const pay = async () => {
    if (!selected) return toast.error("Select a plan to renew");
    const res = await createOrder.mutateAsync({ plan_id: selected });
    setCheckout({ open: true, orderId: res.data.order_id });
  };

  return (
    <div className="space-y-6" data-testid="billing-tenant">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Plan" value={sub?.plan_name || "—"} />
        <Stat label="Status" value={<SubStatusBadge status={sub?.status} testId="tenant-sub-status" />} />
        <Stat label="Days left" value={sub?.days_left ?? 0} accent={sub?.days_left <= 5 ? "text-[#EF4444]" : "text-foreground"} />
        <Stat label="Expires" value={sub?.current_period_end ? fmtDate(sub.current_period_end) : "—"} />
      </div>

      {/* Plan selection */}
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <div className="mb-4 flex items-center gap-2.5">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Renew subscription</h3>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {plans.map((p) => (
            <button key={p.id} data-testid={`renew-plan-${p.period}`} onClick={() => setSelected(p.id)}
              className={cn("rounded-xl border p-4 text-left transition-colors",
                selected === p.id ? "border-white/40 bg-white/[0.04]" : "border-white/[0.08] hover:border-white/20")}>
              <div className="text-sm font-medium text-foreground">{p.name}</div>
              <div className="mt-1 font-display text-xl font-bold">{fmtMoney(p.price, p.currency)}</div>
              <div className="text-xs capitalize text-muted-foreground">per {p.period.replace("ly", "")}</div>
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={pay} disabled={createOrder.isPending} data-testid="renew-submit-btn" className="gap-2 bg-white text-black hover:bg-white/90">
            <RefreshCw className="h-4 w-4" /> Pay with crypto
          </Button>
          {openOrder && (
            <Button onClick={() => setCheckout({ open: true, orderId: openOrder.order_id })} variant="outline" data-testid="resume-order-btn" className="gap-2 border-white/15">
              Resume pending payment
            </Button>
          )}
        </div>
      </div>

      {/* Order history */}
      {!compact && (
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
          <div className="mb-4 flex items-center gap-2.5"><Receipt className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-medium text-foreground">Payment orders</h3></div>
          <div className="space-y-2" data-testid="payment-history">
            {orders.length === 0 && <p className="text-sm text-muted-foreground">No payment orders yet.</p>}
            {orders.map((o) => (
              <button key={o.order_id} onClick={() => setCheckout({ open: true, orderId: o.order_id })}
                data-testid={`order-row-${o.order_id}`}
                className="flex w-full items-center justify-between rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5 text-left text-sm transition-colors hover:border-white/15">
                <div>
                  <div className="text-foreground">{o.plan_name} · {fmtMoney(o.amount, o.currency)} · {o.network}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(o.created_at)}{o.tx_hash ? ` · ${o.tx_hash}` : ""}</div>
                </div>
                <SubStatusBadge status={o.status} />
              </button>
            ))}
          </div>
        </div>
      )}

      <CryptoCheckout open={checkout.open} orderId={checkout.orderId} onOpenChange={(v) => setCheckout((c) => ({ ...c, open: v }))} />
    </div>
  );
}
